import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

const Databases = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Scalable & Resilient Databases - limkc</title>
        <meta name="description" content="Learn about Scalable & Resilient Databases with real-world examples." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
  
      <div className="w-full h-full overflow-auto">
        {/* Banner Image - Full Width */}
        <div className="w-full h-48 overflow-hidden" style={{ marginTop: 'var(--navbar-height)' }}>
          <img
            src="/IMAGE/database.png"
            alt="Database Architecture"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Content - Centered */}
        <div className="w-full flex justify-center">
          <div className="py-6 transition-all duration-300 w-full"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--text-primary)',
              maxWidth: '1200px',
              paddingLeft: '1rem',
              paddingRight: '1rem'
            }}>

            <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Scalable & Resilient Databases</h1>
          <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>
            In cloud architecture, databases need to be scalable, resilient, and optimized for high-performance workloads.
            Choosing the right SQL (RDS, Aurora) or NoSQL (DynamoDB, MongoDB) solution depends on the use case, consistency, and latency requirements.
          </p>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>SQL vs. NoSQL: Choosing the Right Database</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Relational (SQL) databases are ideal for structured data, ACID transactions, and complex queries.
            NoSQL databases are designed for scalability, flexibility, and high-velocity data.
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><b>Amazon RDS (SQL)</b> – Fully managed PostgreSQL, MySQL, and SQL Server.</li>
            <li><b>Amazon Aurora</b> – High-performance SQL database with distributed storage.</li>
            <li><b>Amazon DynamoDB (NoSQL)</b> – Serverless, scalable key-value store.</li>
            <li><b>MongoDB Atlas (NoSQL)</b> – Flexible document-based database.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Scalability Strategies</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><b>Read Replicas</b> – Offload read queries to secondary databases.</li>
            <li><b>Sharding</b> – Split large datasets across multiple nodes.</li>
            <li><b>Connection Pooling</b> – Manage concurrent database connections efficiently.</li>
            <li><b>Auto-Scaling</b> – Use DynamoDB Auto Scaling and Aurora Serverless.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Real World Experience</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            I designed a multi-region, fault-tolerant database system for a SaaS platform.
            The biggest challenges were replication latency, cost optimization, and failover handling.
            Solutions included Aurora Global Database, read replicas, and intelligent query routing.
          </p>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>High Availability & Disaster Recovery</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            To ensure uptime, cloud databases leverage replication, backup strategies, and failover mechanisms.
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><b>Multi-AZ Deployments</b> – Replicates data across multiple availability zones.</li>
            <li><b>Global Databases</b> – Synchronizes across regions for disaster recovery.</li>
            <li><b>Point-in-Time Recovery</b> – Restores databases to any past state.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>DynamoDB Example (Auto-Scaling NoSQL Table)</h2>
          <pre className="p-4 rounded-md mt-2 text-sm" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>
  {`
  resource "aws_dynamodb_table" "example" {
    name           = "MyDynamoDBTable"
    billing_mode   = "PAY_PER_REQUEST"
    hash_key       = "id"
  
    attribute {
      name = "id"
      type = "S"
    }
  
    replication_group {
      region_name = "us-west-2"
    }
  }
  `}
          </pre>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Best Practices</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li>Use Read Replicas for performance optimization.</li>
            <li>Enable Multi-AZ or Global Databases for disaster recovery.</li>
            <li>Utilize query caching (Redis, Memcached) for faster responses.</li>
            <li>Implement automated backups and snapshot policies.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Related Topics</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><a href="/topics/cloud-management" className="text-blue-500 hover:underline">Cloud Management & Multi-Cloud Strategy</a></li>
            <li><a href="/topics/serverless" className="text-blue-500 hover:underline">Serverless & API Gateway</a></li>
          </ul>
  
          <button
            onClick={() => navigate("/projects")}
            className="mt-6 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--text-secondary)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
          >
            ← Back to Topics
          </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Databases;
